import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

const CHAT_SCROLL_ROOT_SELECTOR = '.App .main-content';
const CHAT_NEAR_BOTTOM_THRESHOLD_PX = 120;

export function getChatScrollRoot() {
    return document.querySelector(CHAT_SCROLL_ROOT_SELECTOR);
}

export function isChatNearBottom(root, thresholdPx = CHAT_NEAR_BOTTOM_THRESHOLD_PX) {
    if (!root) return true;
    const distanceFromBottom = root.scrollHeight - root.scrollTop - root.clientHeight;
    return distanceFromBottom <= thresholdPx;
}

export function scrollChatToBottom({ behavior = 'smooth', force = false } = {}) {
    const root = getChatScrollRoot();
    if (!root) return;
    const maxScroll = root.scrollHeight - root.clientHeight;
    root.scrollTo({
        top: Math.max(0, maxScroll),
        behavior: force ? 'auto' : behavior,
    });
}

/**
 * Keeps the viewport scroll pinned when the user is already at the bottom.
 * Call pinToBottom() when the user sends a message to force follow.
 */
export function useAiChatScroll(...scrollDeps) {
    const stickToBottomRef = useRef(true);

    useEffect(() => {
        const root = getChatScrollRoot();
        if (!root) return undefined;

        const onScroll = () => {
            stickToBottomRef.current = isChatNearBottom(root);
        };

        root.addEventListener('scroll', onScroll, { passive: true });
        return () => root.removeEventListener('scroll', onScroll);
    }, []);

    const pinToBottom = useCallback(() => {
        stickToBottomRef.current = true;
        scrollChatToBottom({ behavior: 'auto', force: true });
    }, []);

    const scrollToBottomIfStuck = useCallback((behavior = 'smooth') => {
        if (!stickToBottomRef.current) return;
        scrollChatToBottom({ behavior });
    }, []);

    useLayoutEffect(() => {
        scrollToBottomIfStuck('smooth');
        // scrollToBottomIfStuck is stable (empty-dep useCallback); scrollDeps is a
        // caller-supplied variadic dep list, so it can't be a static array literal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, scrollDeps);

    return { pinToBottom, scrollToBottomIfStuck };
}
